'use client';

import React from 'react';
import styled from 'styled-components';
import { COLORS } from '@/constants/colors';


const FEATURES = [
  {
    id: 0,
    title: 'Double-Layer Woven Cotton Filtration',
    description:
      'Green Filter uses double-layered woven cotton with deep pleats to increase filtration surface area. This allows more air to move freely into the engine while still helping trap harmful particles before they reach critical engine components.',
    imgSrc: '/assets/features/img0.webp',
  },
  {
    id: 1,
    title: 'Steel Mesh Support',
    description:
      'A durable steel mesh surrounds the cotton filter media, adding strength and dent resistance. This helps the filter maintain its shape and performance even under demanding driving conditions.',
    imgSrc: '/assets/features/img1.webp',
  },
  {
    id: 2,
    title: 'Proprietary Filter Oil Technology',
    description:
      'Green Filter\'s specially designed filter oil helps attract and hold dust particles while keeping the cotton media clear for airflow. This helps maintain strong filtration and performance between cleanings.',
    imgSrc: '/assets/features/img2.webp',
  },
  {
    id: 3,
    title: 'Low-Pressure Molding Process',
    description:
      'Some filters use pressure-molding methods that can allow rubber to cover part of the filtering surface. Green Filter uses a careful molding process designed to reduce rubber seepage and preserve airflow through the filter media.',
    imgSrc: '/assets/features/img3.webp',
  },
  {
    id: 4,
    title: 'Custom-Molded Fitment',
    description:
      'Green Filter does not rely on one-size-fits-all designs. Filters are built for specific applications to help provide a proper seal, factory-style fit, and easy installation without unnecessary modification.',
    imgSrc: '/assets/features/img4.webp',
  },
  {
    id: 5,
    title: 'Race-Proven Manufacturing',
    description:
      'Green Filter technology was developed through years of performance and motorsports experience. That same focus on airflow, durability, and filtration is built into every filter made for street and performance vehicles.',
    imgSrc: '/assets/features/img5.webp',
  },
];

const HomeElement3 = () => {
  return (
    <Section>
      <Container>
        <HeadingBlock>
          <Headline>What Makes Green Filter a Better Performance Air Filter?</Headline>
          <Accent />
          <SubHeadline>
            Not all air filters are built the same. Green Filter uses advanced materials, precision molding, and race-proven manufacturing methods to create high-performance car air filters that deliver airflow, filtration, and long-term durability.
          </SubHeadline>
        </HeadingBlock>

        <FeaturesGrid>
          {FEATURES.map((feature) => (
            <FeatureCard key={feature.id}>
              <IconContainer>
                <Icon src={feature.imgSrc} alt={feature.title} />
              </IconContainer>
              <CardTitle>{feature.title}</CardTitle>
              <CardText>{feature.description}</CardText>
            </FeatureCard>
          ))}
        </FeaturesGrid>
      </Container>
    </Section>
  );
};

export default React.memo(HomeElement3);

const Section = styled.section`
  background: #fff;
  padding: 80px 24px;

  @media (max-width: 1024px) {
    padding: 64px 24px;
  }

  @media (max-width: 767px) {
    padding: 48px 16px;
  }
`;

const Container = styled.div`
  max-width: 1140px;
  margin: 0 auto;
`;

const HeadingBlock = styled.div`
  max-width: 640px;
  margin: 0 auto 56px;
  text-align: center;

  @media (max-width: 767px) {
    margin-bottom: 40px;
  }
`;

const Headline = styled.h2`
  font-family: Lato, sans-serif;
  font-size: 32px;
  font-weight: 800;
  color: #1a1a1a;
  margin: 0 0 14px;
  line-height: 1.2;

  @media (max-width: 767px) {
    font-size: 24px;
  }
`;

const Accent = styled.div`
  width: 56px;
  height: 3px;
  background: ${COLORS.brandGreen};
  border-radius: 2px;
  margin: 0 auto 20px;
`;

const SubHeadline = styled.p`
  font-family: Lato, sans-serif;
  font-size: 16px;
  font-weight: 400;
  color: #555;
  margin: 0;
  line-height: 1.6;

  @media (max-width: 767px) {
    font-size: 15px;
  }
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 32px;

  @media (max-width: 991px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 24px;
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const FeatureCard = styled.div`
  background: #fff;
  border: 1px solid #ebebeb;
  border-radius: 16px;
  overflow: hidden;
  text-align: center;
  transition: all 0.3s ease;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
    border-color: ${COLORS.brandGreen};
  }
`;

const IconContainer = styled.div`
  width: 100%;
  height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8f8f8;
  padding: 24px;
  transition: all 0.3s ease;

  ${FeatureCard}:hover & {
    background: #e8f5e9;
  }

  @media (max-width: 767px) {
    height: 160px;
    padding: 20px;
  }
`;

const Icon = styled.img`
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  display: block;
  object-fit: contain;
`;

const CardTitle = styled.h3`
  font-family: Lato, sans-serif;
  font-size: 20px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 12px;
  line-height: 1.3;
  padding: 24px 24px 0;

  @media (max-width: 767px) {
    font-size: 18px;
    padding: 20px 20px 0;
  }
`;

const CardText = styled.p`
  font-family: Lato, sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: #555;
  margin: 0;
  line-height: 1.6;
  padding: 0 24px 24px;

  @media (max-width: 767px) {
    font-size: 13px;
    padding: 0 20px 20px;
  }
`;
